import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import useLabels from "../../hooks/useLabels";
import {
  deleteLot,
  getBatchArrival,
  getLotHistory,
  getLots,
  getLotSubstates,
  getOperationById,
  getOperations,
  getTanks,
  getWineLabels,
} from "../../services/utils";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { removeL, setLots } from "../../context/lots/lotsSlice";
import { setTanks } from "../../context/tanks/tanksSlice";
import { getLocaleObject } from "../../context/configs/configsSlice";
import {
  selectActiveWinery,
  selectAnalysis,
  selectConfigs,
  selectLots,
  selectOperations,
  selectTanks,
  selectUsers,
  selectWineLabels,
} from "../../context/selectors";
import { Label } from "../FormComponents";
import {
  Button,
  BatchColumn,
  CTA,
  CellarmanColumn,
  TankColumn,
  ConfirmationModal,
  Dropdown,
  RecapTankIcon,
  Tooltip,
} from "../Generic";

import "./styles/index.scss";
import AnalysisGrid from "../Home/AnalysisGrid";
import { ROLES } from "../../constants/base";
import useAuth from "../../hooks/useAuth";
import { format, formatDistanceToNow, intervalToDuration } from "date-fns";
import SecondaryBar from "../SecondaryBar";
import NextOperations from "../TankDetail/NextOperations";
import { toast } from "react-toastify";
import { setOperations } from "../../context/operations/operationsSlice";
import LotLabelTags from "../LotLabelTags";
import { DestTankDetail, TreatmentsLabel } from "../OperationsDetail/Details";
import {
  aggregateDrugConcentrations,
  flattenDrugIngredients,
  getWineColorClassName,
} from "../../services/common";
import { toastAxios } from "../../services/toastAxios";
import { setWineLabels } from "../../context/wineLabels/wineLabelsSlice";
import CacheManager from "../../services/cacheManager";
import { Icon } from "../../components/Icon";

/**
 * SubstateHistoryTimeline - Componente per visualizzare la storia dei cambiamenti di substate di un lotto
 *
 * Props:
 * - substateHistory: Array di record da getSubstateHistoryDB
 * - batchColor: Colore dell'uva (WHITE, RED, ROSÉ)
 * - batchState: Stato attuale del lotto
 * - isLoading: Boolean per stato di caricamento
 * - error: Errore durante il caricamento
 */
const SubstateHistoryTimeline = ({
  substateHistory = [],
  batchColor = null,
  batchState = null,
  isLoading = false,
  error = null,
  configs,
  getLabel,
}) => {
  const locale = getLocaleObject(configs.locale);

  // Configurazione colori CSS per i substati
  const substateColorClasses = {
    DESTEMMING: "substate_destemming",
    MACERATING: "substate_macerating",
    FERMENTING: "substate_fermenting",
    POST_FERMENTATION: "substate_post_fermentation",
    RACKING_READY: "substate_racking_ready",
    AGING: "substate_aging",
    STABILIZING: "substate_stabilizing",
    CLARIFYING: "substate_clarifying",
    FILTERING: "substate_filtering",
    BOTTLING_READY: "substate_bottling_ready",
    BOTTLE_AGING: "substate_bottle_aging",
    READY_TO_SHIP: "substate_ready_to_ship",
  };

  // Configurazione colori per border left
  const substateLeftBorderClasses = {
    DESTEMMING: "substate_border_destemming",
    MACERATING: "substate_border_macerating",
    FERMENTING: "substate_border_fermenting",
    POST_FERMENTATION: "substate_border_post_fermentation",
    RACKING_READY: "substate_border_racking_ready",
    AGING: "substate_border_aging",
    STABILIZING: "substate_border_stabilizing",
    CLARIFYING: "substate_border_clarifying",
    FILTERING: "substate_border_filtering",
    BOTTLING_READY: "substate_border_bottling_ready",
    BOTTLE_AGING: "substate_border_bottle_aging",
    READY_TO_SHIP: "substate_border_ready_to_ship",
  };

  // Formatta la durata
  const formatDurationTime = (startDate, endDate) => {
    if (!startDate) return null;

    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : new Date();

    const duration = intervalToDuration({ start, end });

    if (duration.hours === 0 && duration.minutes === 0) {
      return `${duration.seconds || 0}s`;
    }

    const parts = [];
    if (duration.days) parts.push(`${duration.days}g`);
    if (duration.hours) parts.push(`${duration.hours}h`);
    if (duration.minutes) parts.push(`${duration.minutes}m`);

    return parts.join(" ");
  };

  // Ordina per data (più recenti prima)
  const sortedHistory = useMemo(() => {
    return [...substateHistory].sort(
      (a, b) => new Date(b.started_at) - new Date(a.started_at),
    );
  }, [substateHistory]);

  if (isLoading) {
    return (
      <div className="substate_loading">
        <div className="substate_spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="substate_error">
        <div className="substate_error_icon">
          <svg viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <div className="substate_error_content">
          <p className="substate_error_title">
            {getLabel("substateHistoryErrorTitle")}
          </p>
          <p className="substate_error_message">{error}</p>
        </div>
      </div>
    );
  }

  if (!sortedHistory || sortedHistory.length === 0) {
    return (
      <div className="substate_empty">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <p className="substate_empty_text">
          {getLabel("substateHistoryEmpty")}
        </p>
      </div>
    );
  }

  return (
    <div className="substate_container">
      {/* Header */}
      <div className="substate_header">
        <h3 className="substate_header_title">
          {getLabel("substateHistoryTitle")}
        </h3>
        <p className="substate_header_subtitle">
          {sortedHistory.length}{" "}
          {sortedHistory.length === 1
            ? getLabel("substateHistoryCountSingular")
            : getLabel("substateHistoryCountPlural")}
        </p>
      </div>

      {/* Timeline */}
      <div className="substate_timeline">
        {sortedHistory.map((record, index) => {
          const colorClass =
            substateColorClasses[record.new_substate] || "substate_default";
          const borderClass =
            substateLeftBorderClasses[record.new_substate] ||
            "substate_border_default";
          const isActive = !record.ended_at;

          return (
            <div key={record.id} className="substate_timeline_item">
              {/* Timeline line */}
              {index < sortedHistory.length - 1 && (
                <div className="substate_timeline_line"></div>
              )}

              {/* Timeline dot */}
              <div
                className={`substate_timeline_dot ${isActive ? "substate_dot_active" : "substate_dot_inactive"}`}
              ></div>

              {/* Card */}
              <div className={`substate_card ${borderClass}`}>
                {/* Header con substate */}
                <div className="substate_card_header">
                  <div className="substate_card_header_left">
                    <div className="substate_card_header_labels">
                      <span className={`substate_badge ${colorClass}`}>
                        {getLabel(`lotSubstate_${record.new_substate}`)}
                      </span>
                      {isActive && (
                        <span className="substate_badge_active">
                          <span className="icon"><Icon icon="fa-circle" className="pulse_icon" /></span>
                          {getLabel("substateActive")}
                        </span>
                      )}
                    </div>
                    {record.old_substate && (
                      <p className="substate_card_from">
                        {getLabel("substateFrom")}:{" "}
                        <span>
                          {getLabel(`lotSubstate_${record.old_substate}`)}
                        </span>
                      </p>
                    )}
                  </div>
                  <div className="substate_card_header_right">
                    <p className="substate_card_date">
                      {format(new Date(record.started_at), "dd MMM yyyy", {
                        locale: locale,
                      })}
                    </p>
                    <p className="substate_card_time">
                      {format(new Date(record.started_at), "HH:mm:ss", {
                        locale: locale,
                      })}
                    </p>
                  </div>
                </div>

                {/* Durata */}
                {formatDurationTime(record.started_at, record.ended_at) && (
                  <div className="substate_card_duration">
                    <svg viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00-.293.707l-2.828 2.829a1 1 0 101.414 1.414L10 11.414V6z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="substate_duration_label">
                      {getLabel("substateDuration")}:{" "}
                      {formatDurationTime(record.started_at, record.ended_at)}
                    </span>
                    {record.ended_at && (
                      <span className="substate_duration_end">
                        ({getLabel("substateUntil")}{" "}
                        {format(new Date(record.ended_at), "dd MMM HH:mm", {
                          locale: locale,
                        })}
                        )
                      </span>
                    )}
                  </div>
                )}

                {/* Trigger information */}
                <div className="substate_info_box">
                  <div className="substate_info_grid">
                    {/* Trigger Type */}
                    <div className="substate_info_item">
                      <p className="substate_info_label">
                        {getLabel("substateType")}:
                      </p>
                      <span className="substate_info_badge substate_info_badge_type">
                        {record.trigger_type === "OPERATION"
                          ? getLabel("substateTriggerOperation")
                          : record.trigger_type === "MANUAL"
                            ? getLabel("substateTriggerManual")
                            : record.trigger_type === "AUTOMATIC"
                              ? getLabel("substateTriggerAutomatic")
                              : record.trigger_type}
                      </span>
                    </div>

                    {/* Operation */}
                    {record.operation_type && (
                      <div className="substate_info_item">
                        <p className="substate_info_label">
                          {getLabel("substateOperation")}:
                        </p>
                        <div className="substate_info_operation">
                          <span className="substate_info_badge substate_info_badge_operation">
                            {getLabel(
                              `operationType${record.operation_type}`,
                            ) || record.operation_type}
                          </span>
                          {record.operation_subtype && (
                            <span className="substate_info_badge substate_info_badge_subtype">
                              {getLabel(
                                `operationSubtype${record.operation_subtype}`,
                              ) || record.operation_subtype}
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* User */}
                    {record.username && (
                      <div className="substate_info_item">
                        <p className="substate_info_label">
                          {getLabel("substateUser")}:
                        </p>
                        <span className="substate_info_badge substate_info_badge_user">
                          {record.username}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Metadata e Notes */}
                <div className="substate_card_extras">
                  {false && record.metadata &&
                    Object.keys(record.metadata).length > 0 && (
                      <div className="substate_metadata">
                        <p className="substate_metadata_label">
                          {getLabel("substateMetadata")}:
                        </p>
                        <div className="substate_metadata_content">
                          {JSON.stringify(record.metadata, null, 2)}
                        </div>
                      </div>
                    )}

                  {record.notes && (
                    <div className="substate_notes">
                      <p className="substate_notes_label">
                        {getLabel("substateNotes")}:
                      </p>
                      <p className="substate_notes_content">{record.notes}</p>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="substate_card_footer">
                  <div className="substate_card_footer_info">
                    {getLabel("substateOperationId")}:{" "}
                    {record.triggered_by_operation_id || "—"} |{" "}
                    {getLabel("substateBatchId")}: {record.batch_id}
                  </div>
                  {record.triggered_by_operation_id && record.operation_type && (
                    <a
                      href={`/programming/history/${record.operation_type.toLowerCase()}/${record.triggered_by_operation_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="substate_card_footer_link"
                      title={getLabel("goToDetail")}
                    >
                      <Icon icon="fa-external-link-alt" />
                      {getLabel("goToDetail")}
                    </a>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const LotLineage = ({ relatives = [], label = "" }) => {
  const [getLabel] = useLabels();
  const navigate = useNavigate();
  const configs = useSelector(selectConfigs);
  const locale = getLocaleObject(configs.locale);

  const uniqueRelativesById = Array.from(
    new Map(relatives?.map((item) => [item?.id, item])).values(),
  );

  // Mappa degli stati ai colori
  const getStateColorClass = (state) => {
    const stateColors = {
      GRAPE: "lineage_state_grape",
      MUST: "lineage_state_must",
      WINE: "lineage_state_wine",
      BOTTLED: "lineage_state_bottled",
    };
    return stateColors[state] || "lineage_state_default";
  };

  // Mappa delle operation types ai colori
  const getOperationColorClass = (operationType) => {
    const operationColors = {
      DESTEMMING: "lineage_operation_destemming",
      RACKING: "lineage_operation_racking",
      CUT: "lineage_operation_cut",
      TRANSFER: "lineage_operation_transfer",
      BOTTLING: "lineage_operation_bottling",
      FILTERING: "lineage_operation_filtering",
      WINEMAKING: "lineage_operation_winemaking",
      DECANT: "lineage_operation_decant",
    };
    return operationColors[operationType] || "lineage_operation_default";
  };

  // Icone per operation type
  const getOperationIcon = (operationType) => {
    const icons = {
      DESTEMMING: "fa-leaf",
      RACKING: "fa-arrows-rotate",
      CUT: "fa-scissors",
      TRANSFER: "fa-arrow-right-arrow-left",
      BOTTLING: "fa-wine-bottle",
      FILTERING: "fa-filter",
      WINEMAKING: "fa-wine-glass",
      DECANT: "fa-droplet",
    };
    return icons[operationType] || "fa-circle-dot";
  };

  // Ottieni la classe CSS per il colore del vino
  const getWineColorAccent = (color) => {
    const colorClasses = {
      RED: "lineage_accent_red",
      WHITE: "lineage_accent_white",
      ROSE: "lineage_accent_rose",
    };
    return colorClasses[color] || "";
  };

  // Formatta la quantità con unità corretta
  const formatQuantity = (quantity, state) => {
    if (!quantity) return null;
    const unit = state === "GRAPE" ? "Kg" : "L";
    return `${Math.round(quantity)} ${unit}`;
  };

  // Ottieni la varietà principale dalla composizione
  const getMainVariety = (composition) => {
    if (!composition || composition.length === 0) return null;
    // Ordina per percentuale decrescente e prendi la prima
    const sorted = [...composition].sort((a, b) => (b.percentage || 0) - (a.percentage || 0));
    return sorted[0];
  };

  // Ottieni l'icona corretta in base allo stato del lotto
  const getLotIcon = (state) => {
    const stateIcons = {
      GRAPE: "fa-leaf",        // Uva → Foglia
      MUST: "fa-flask",        // Mosto → Beuta/Flask
      WINE: "fa-wine-glass",   // Vino → Bicchiere
      BOTTLED: "fa-wine-bottle", // Imbottigliato → Bottiglia
    };
    return stateIcons[state] || "fa-wine-glass";
  };

  if (!uniqueRelativesById || uniqueRelativesById.length === 0) {
    return null;
  }

  return (
    <div className="lot_lineage_wrapper">
      <h5 className="lot_lineage_label">{label}</h5>
      <div className="lot_lineage_cards">
        {uniqueRelativesById?.map((relative, index) => {
          const stateClass = getStateColorClass(relative?.state);
          const operationClass = getOperationColorClass(relative?.operation_type);
          const operationIcon = getOperationIcon(relative?.operation_type);
          const colorAccent = getWineColorAccent(relative?.color);
          const mainVariety = getMainVariety(relative?.composition);
          const lotIcon = getLotIcon(relative?.state);

          return (
            <div
              key={`lot-relative-${relative?.id}-${index}`}
              className={`lot_lineage_card ${colorAccent}`}
              onClick={() => navigate(`/lots/${relative?.id}`)}
            >
              {/* Header con nome, ID e freccia */}
              <div className="lot_lineage_card_header">
                <div className="lot_lineage_card_name">
                  <Icon                     icon={lotIcon}
                    className="lot_lineage_card_icon"
                  />
                  <div className="lot_lineage_card_name_wrapper">
                    <span className="lot_lineage_card_name_text">
                      {relative?.name || "—"}
                    </span>
                    <span className="lot_lineage_card_id">ID: {relative?.id}</span>
                  </div>
                </div>
                <Icon                   icon="fa-arrow-right"
                  className="lot_lineage_card_arrow"
                  title={getLabel("goToDetail")}
                />
              </div>

              {/* Badges per stato e operation */}
              <div className="lot_lineage_card_badges">
                {relative?.state && (
                  <span className={`lot_lineage_badge ${stateClass}`}>
                    {getLabel(`lotState_${relative?.state}`)}
                  </span>
                )}
                {relative?.operation_type && (
                  <span className={`lot_lineage_badge ${operationClass}`}>
                    <Icon icon={operationIcon} />
                    {getLabel(`operationType${relative?.operation_type}`) ||
                      relative?.operation_type}
                  </span>
                )}
              </div>

              {/* Info grid: quantità, data, qualità */}
              <div className="lot_lineage_card_info_grid">
                {/* Quantità */}
                {relative?.quantity && (
                  <div className="lot_lineage_info_item">
                    <Icon icon="fa-flask" className="lot_lineage_info_icon" />
                    <div className="lot_lineage_info_content">
                      <span className="lot_lineage_info_label">
                        {getLabel("lotQuantity")}
                      </span>
                      <span className="lot_lineage_info_value">
                        {formatQuantity(relative?.quantity, relative?.state)}
                      </span>
                    </div>
                  </div>
                )}

                {/* Data operazione */}
                {relative?.operation_date && (
                  <div className="lot_lineage_info_item">
                    <Icon icon="fa-calendar" className="lot_lineage_info_icon" />
                    <div className="lot_lineage_info_content">
                      <span className="lot_lineage_info_label">
                        {getLabel("operationCompetionDate")}
                      </span>
                      <span className="lot_lineage_info_value">
                        {format(new Date(relative?.operation_date), "dd MMM yyyy", {
                          locale: locale,
                        })}
                      </span>
                    </div>
                  </div>
                )}

                {/* Qualità */}
                {relative?.quality && (
                  <div className="lot_lineage_info_item">
                    <Icon icon="fa-star" className="lot_lineage_info_icon" />
                    <div className="lot_lineage_info_content">
                      <span className="lot_lineage_info_label">
                        {getLabel("lotQuality")}
                      </span>
                      <span className="lot_lineage_info_value">
                        {getLabel(`lotQuality_${relative?.quality}`) || relative?.quality}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Composizione principale (varietà) */}
              {mainVariety && (
                <div className="lot_lineage_composition">
                  <Icon icon="fa-leaf" className="lot_lineage_composition_icon" />
                  <div className="lot_lineage_composition_text">
                    <span className="lot_lineage_composition_variety">
                      {mainVariety?.variety}
                    </span>
                    {mainVariety?.percentage < 100 && (
                      <span className="lot_lineage_composition_percentage">
                        ({mainVariety?.percentage}%)
                      </span>
                    )}
                    <span className="lot_lineage_composition_meta">
                      {mainVariety?.origin && `${mainVariety?.origin}`}
                      {mainVariety?.year && ` • ${mainVariety?.year}`}
                    </span>
                  </div>
                </div>
              )}

              {/* Label (DOC, DOCG, etc.) */}
              {relative?.label && relative?.label?.length > 0 && (
                <div className="lot_lineage_labels">
                  {relative?.label?.map((lbl, idx) => (
                    <span key={`label-${idx}`} className="lot_lineage_label_badge">
                      <Icon icon="fa-certificate" />
                      {getLabel(`lotLabel_${lbl}`)}
                    </span>
                  ))}
                </div>
              )}

              {/* Note (se presente) */}
              {relative?.note && relative?.note?.trim()?.length > 0 && (
                <div className="lot_lineage_card_note">
                  <Icon icon="fa-sticky-note" />
                  <span className="lot_lineage_card_note_text">
                    {relative?.note}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const LotInfo = ({
  lot,
  tankList,
  substates,
  wineLabels,
  isDetail = true,
  closeAdditionalInfo = () => null,
  tankId = null,
}) => {

  const [getLabel] = useLabels();
  const navigate = useNavigate();
  const configs = useSelector(selectConfigs);
  const operations = useSelector(selectOperations);
  const analysisProtocols = useSelector(selectAnalysis);
  const [labelTankId, setLabelTankId] = useState(tankList?.[0]?.id || null);
  const [drugConcentrations, setDrugConcentrations] = useState([]);
  const [isDrugConcentration, setIsDrugConcentration] = useState(true);
  const [mergedBatchVarieties, setMergedBatchVarieties] = useState([]);
  const [isMergedBatchVarieties, setIsMergedBatchVarieties] = useState(false);
  const [isAdjuvant, setIsAdjuvant] = useState(false);
  const [showSubstateHistory, setShowSubstateHistory] = useState(false);
  const [selectedTankIdForSubstates, setSelectedTankIdForSubstates] = useState(null);
  const [selectedLabel, setSelectedLabel] = useState(null);

  const wineColor = getWineColorClassName(lot?.color);

  const IN_PROGRESS_STATES = [
    "OXYGENATING",
    "FILTERING",
    "STABILIZING",
    "CLARIFYING",
    "REASSEMBLING",
    "TEMP_HOMOGENIZING"
  ];

  const isSubstateInProgress = (substate) => {
    return IN_PROGRESS_STATES.includes(substate);
  };

  useEffect(() => {
    const drugsComposition = lot?.concentrations;
    if (Array.isArray(drugsComposition) && drugsComposition?.length > 0) {
      const drugsCompositionByTankId = drugsComposition?.filter(
        (c) => c?.tank_id === Number(labelTankId),
      );
      const drugIngredients = flattenDrugIngredients(drugsCompositionByTankId);
      setDrugConcentrations(
        aggregateDrugConcentrations(
          isDrugConcentration,
          isAdjuvant,
          drugIngredients,
        ),
      );
    }
    if (!labelTankId) {
      setLabelTankId(tankList?.[0]?.tank_id);
    }
  }, [lot, isDrugConcentration, isAdjuvant, labelTankId, tankList]);

  useEffect(() => {
    if (!lot?.composition || !Array.isArray(lot?.composition)) return;
    const lotComposition = [
      ...lot?.composition?.filter((c) =>
        tankId ? c?.tank_id === tankId : true,
      ),
    ]
      ?.map((c) =>
        tankId !== null ? { ...c, percentage: c.tank_percentage } : c,
      )
      ?.sort((a, b) => (a?.variety || "")?.localeCompare(b?.variety || ""));

    let finalComposition = lotComposition;
    if (isMergedBatchVarieties) {
      const mergedComp = [];
      const seen = new Map();

      for (const item of lotComposition) {
        const { variety, year, origin, quantity = 0, percentage = 0 } = item;

        // Track by variety
        if (!seen.has(variety)) {
          const data = {
            variety,
            year,
            origin,
            quantity,
            percentage,
            subKeys: new Set([`${year}|${origin}`]),
            subs: [], // to store secondary rows,
          };
          seen.set(variety, data);
          mergedComp.push(data);
        } else {
          const existing = seen.get(variety);
          existing.quantity += quantity;
          existing.percentage += percentage;

          const key = `${year}|${origin}`;
          if (!existing.subKeys.has(key)) {
            existing.subKeys.add(key);
            existing.subs.push({
              year,
              origin,
              quantity,
              hideVariety: true,
            });
          }
        }
      }

      // Adjust percentages so that the rounded total is exactly 100%
      const mainItems = mergedComp;
      let total = 0;
      for (let i = 0; i < mainItems.length; i++) {
        let rounded = Math.round(mainItems[i].percentage);
        if (i === mainItems.length - 1) {
          rounded = 100 - total;
        } else {
          total += rounded;
        }
        mainItems[i].percentage = rounded;
      }

      // Group main + secondary rows and sort by total quantity
      const grouped = [];
      for (const item of mainItems) {
        grouped.push([
          {
            variety: item.variety,
            year: item.year,
            origin: item.origin,
            quantity: item.quantity,
            percentage: item.percentage,
          },
          ...item.subs,
        ]);
      }
      // Sort groups  by total quantity descending (from main entry)
      grouped.sort((a, b) => (b[0].quantity || 0) - (a[0].quantity || 0));

      // Flatten result
      finalComposition = grouped.flat();
    } else {
      // Sort non-merged composition by quantity descending
      finalComposition = [...lotComposition]
        ?.map((l) => {
          return {
            ...l,
            tank_name: tankList?.find((t) => t?.id === l?.tank_id)?.name || "",
          };
        })
        ?.sort((a, b) => (b.quantity || 0) - (a.quantity || 0));
    }

    // console.log('lotComposition', lotComposition, 'mergedBatchVarieties', finalComposition)
    // Single assignment for both branches
    setMergedBatchVarieties(finalComposition);
  }, [lot, isMergedBatchVarieties]);

  const filteredDrugs = drugConcentrations?.filter((c) => {
    if (isAdjuvant) return true;
    return c?.type !== "ADJUVANT";
  });

  const getTankData = (tank) => {
    const filteredComp =
      (lot?.composition || [])?.filter((c) => c?.tank_id === tank?.id) || [];
    return {
      ...tank,
      is_src: true,
      planned_quantity: 0,
      old_quantity: tank?.quantity,
      composition: filteredComp?.map((f) => {
        return { ...f, percentage: f?.tank_percentage };
      }),
    };
  };

  const isSameVarietyOriginYearDifferentTank = () => {
    return mergedBatchVarieties.some((e, i) =>
      mergedBatchVarieties.some(
        (f, j) =>
          i !== j &&
          e.variety === f.variety &&
          e.origin === f.origin &&
          e.year === f.year &&
          e.tank_id !== f.tank_id,
      ),
    );
  };

  // Filtro substates in base al tankId
  const filteredSubstates = useMemo(() => {
    if (!substates || substates.length === 0) return [];

    // Caso 1: tankId specificato (es. TankDetail) → filtra per quel tank
    if (tankId !== null) {
      return substates.filter(s => s.tank_id === tankId);
    }

    // Caso 2: tankId è null (LotDetail generale)
    // Trova tutti i tank unici nei substates
    const uniqueTankIds = [...new Set(substates.map(s => s.tank_id).filter(Boolean))];

    // Se c'è un solo tank, mostra tutti i substates
    if (uniqueTankIds.length <= 1) {
      return substates;
    }

    // Se ci sono tank diversi, filtra per il tank selezionato
    // Default: tank più recente (ultimo started_at)
    if (selectedTankIdForSubstates === null) {
      // Trova il substato più recente
      const mostRecent = [...substates].sort((a, b) =>
        new Date(b.started_at) - new Date(a.started_at)
      )[0];
      setSelectedTankIdForSubstates(mostRecent?.tank_id);
      return substates.filter(s => s.tank_id === mostRecent?.tank_id);
    }

    return substates.filter(s => s.tank_id === selectedTankIdForSubstates);
  }, [substates, tankId, selectedTankIdForSubstates]);

  // Calcola i tank unici per il selettore
  const uniqueTanksForSelector = useMemo(() => {
    if (!substates || substates.length === 0 || tankId !== null) return [];

    const tankIds = [...new Set(substates.map(s => s.tank_id).filter(Boolean))];

    if (tankIds.length <= 1) return [];

    // Crea un array di oggetti con informazioni sui tank
    return tankIds.map(tId => {
      const tankData = tankList?.find(t => t.id === tId);
      const substatesForTank = substates.filter(s => s.tank_id === tId);
      const mostRecentSubstate = [...substatesForTank].sort((a, b) =>
        new Date(b.started_at) - new Date(a.started_at)
      )[0];

      return {
        id: tId,
        name: tankData?.name || `Tank ${tId}`,
        count: substatesForTank.length,
        mostRecentDate: mostRecentSubstate?.started_at
      };
    }).sort((a, b) => new Date(b.mostRecentDate) - new Date(a.mostRecentDate));
  }, [substates, tankId, tankList]);

  // // Helper function
  // const getProgressPercentage = (startDate, endDate) => {
  //   const now = new Date().getTime();
  //   const start = new Date(startDate).getTime();
  //   const end = new Date(endDate).getTime();

  //   const totalDuration = end - start;
  //   const elapsedTime = now - start;
  //   const percentage = (elapsedTime / totalDuration) * 100;

  //   return Math.min(100, Math.max(0, percentage)).toFixed(1);
  // };

  // Find lab analysis operation for this lot
  const getLabAnalysisOperation = () => {
    if (!lot?.lab_analysis_protocol_id || !operations) return null;

    const labAnalysisOp = operations.find(op =>
      op.type === "ANALYSIS" &&
      op?.protocol_id === lot?.lab_analysis_protocol_id &&
      op.src_tanks?.some(tank => tank.batch_id === lot.batch_id)
    );

    return labAnalysisOp;
  };

  // Get protocol by ID
  const getProtocolById = (protocolId) => {
    if (!analysisProtocols || !protocolId) return null;
    return analysisProtocols.find(p => p.protocols_id === protocolId);
  };

  // Controlla se un oggetto ha almeno un valore non-vuoto
  const hasAnyValue = (obj) => {
    if (!obj || typeof obj !== 'object') return false;
    return Object.values(obj).some(v => v !== null && v !== undefined && v !== '');
  };

  return (
    <div className="lot_info_container">
      {/* HEADER SECTION WITH MAIN INFO */}
      {isDetail && (
        <div className={`lot_info_header_section ${lot?.color?.toLowerCase?.()}`}>
          <div className="header_title">
            <Icon               icon={lot?.state === "GRAPE" ? "fa-leaf" : lot?.state === "MUST" ? "fa-flask" : "fa-wine-glass"}
              className="header_icon"
            />
            {lot?.name || "Lotto"}
          </div>
          <div className="header_meta">
            {lot?.quality && (
              <div className="header_meta_item">
                <span className="meta_label">{getLabel("lotQuality")}</span>
                <span className="meta_badge">{getLabel(`lotQuality_${lot.quality}`)}</span>
              </div>
            )}
            {lot?.state && (
              <div className="header_meta_item">
                <span className="meta_label">{getLabel("lotState")}</span>
                <span className="meta_badge">{getLabel(`lotState_${lot.state}`)}</span>
              </div>
            )}
            {lot?.color && (
              <div className="header_meta_item">
                <span className="meta_label">{getLabel("lotColor")}</span>
                <span className="meta_badge">{getLabel(`lotColor_${lot.color}`)}</span>
              </div>
            )}
            {lot?.product_line && (
              <div className="header_meta_item">
                <span className="meta_label">{getLabel("lotProductLine")}</span>
                <span className="meta_badge">{getLabel(`lotProductLine_${lot.product_line}`)}</span>
              </div>
            )}
            {lot?.actual_quantity && (
              <div className="header_meta_item">
                <span className="meta_label">{getLabel("lotQuantity")}</span>
                <span className="meta_value">{`${lot.actual_quantity} ${lot?.state === "GRAPE" ? "Kg" : "L"}`}</span>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="panel_wrapper">
        <div className="left_panel">
          {lot?.label && lot?.label?.length > 0 && (
            <>
              {!isDetail && (
                <div className={`additional_info_container`}>
                  <p
                    className="close_additional"
                    onClick={() => closeAdditionalInfo()}
                  >
                    <Icon                       icon={"fa-arrow-left"}
                      size={"xl"}
                      className="icon"
                    />{" "}
                  </p>
                  <h4 className="aside_title">
                    {getLabel("lotTitlePage", { name: "" })}
                  </h4>
                  <LotLabelTags
                    tags={lot?.label}
                    color={lot?.color?.toLowerCase?.()}
                    isIcon={true}
                    isTitle={true}
                    isDetail={false}
                    onTagClick={setSelectedLabel}
                    selectedTag={selectedLabel}
                  />
                </div>
              )}
              {isDetail && (
                <div>
                  <div className="lot_info_section_title">
                    <Icon icon="fa-certificate" className="section_icon" />
                    {getLabel("lotLabel")}
                  </div>
                  <LotLabelTags
                    tags={lot?.label}
                    color={lot?.color?.toLowerCase?.()}
                    isIcon={true}
                    isTitle={true}
                    isDetail={true}
                    onTagClick={setSelectedLabel}
                    selectedTag={selectedLabel}
                  />
                </div>
              )}
            </>
          )}

          {isDetail && selectedLabel && (
            <div className="lot_label_details">
              <div className="lot_info_section_title">
                <Icon icon="fa-scroll" className="section_icon" />
                {getLabel("disciplinary")}
              </div>
              {(() => {
                const wineLabel = wineLabels?.wine?.find((w) => w?.id === selectedLabel) || null;
                if (!wineLabel) return null;

                const limits = wineLabel?.limits || [];
                const refinements = wineLabel?.refinements || [];
                const colorCount = limits?.filter(l => l?.type === "COLOR" || l?.type === "OTHER_COLOR")?.length || 0;

                return (
                  <div className="lot_lineage_cards">
                    {/* Wine Composition Limits */}
                    {limits?.length > 0 && (
                      <div className="lot_lineage_card">
                        <div className="lot_lineage_card_header">
                          <div className="lot_lineage_card_name">
                            <Icon icon="fa-flask" className="lot_lineage_card_icon" />
                            <span className="lot_lineage_card_name_text">{getLabel("wineComposition")}</span>
                          </div>
                        </div>
                        <div className="lot_lineage_card_info_grid">
                          {limits?.map((l, idx) => (
                            <div key={`limit-${idx}`} className="lot_lineage_info_item">
                              <Icon icon="fa-check" className="lot_lineage_info_icon" />
                              <div className="lot_lineage_info_content">
                                <span className="lot_lineage_info_label">
                                  {l?.type === "RANGE" && getLabel("lotVarieties")}
                                  {l?.type === "COLOR" && getLabel("lotColor")}
                                  {l?.type === "OTHER_COLOR" && getLabel("lotOtherVarieties")}
                                  {l?.type === "SUM" && getLabel("lotVarietiesSum")}
                                </span>
                                <span className="lot_lineage_info_value">
                                  {l?.type === "RANGE" && `${l?.wines?.[0] || ""} ${l?.min * 100 === l?.max * 100 ? `${l?.min * 100 || 0}` : `${l?.min * 100 || 0} - ${l?.max * 100 || 100}`}%`}
                                  {l?.type === "COLOR" && (colorCount === 1 ? `${getLabel(`lotColor_${l?.color}`) || ""}` : `${getLabel(`lotColor_${l?.color}`) || ""} ${l?.min * 100 === l?.max * 100 ? `${l?.min * 100 || 0}` : `${l?.min * 100 || 0} - ${l?.max * 100 || 100}`}%`)}
                                  {l?.type === "OTHER_COLOR" && (colorCount === 1 ? `${getLabel("otherVarieties")} ${getLabel(`lotColor_${l?.color}`) || ""}` : `${getLabel("otherVarieties")} ${getLabel(`lotColor_${l?.color}`) || ""} ${l?.min * 100 === l?.max * 100 ? `${l?.min * 100 || 0}` : `${l?.min * 100 || 0} - ${l?.max * 100 || 100}`}%`)}
                                  {l?.type === "SUM" && `${l?.wines?.[0] || ""} ${l?.min * 100 === l?.max * 100 ? `${l?.min * 100 || 0}` : `${l?.min * 100 || 0} - ${l?.max * 100 || 100}`}%`}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Refinements/Aging */}
                    {refinements?.length > 0 && (
                      <div className="lot_lineage_card">
                        <div className="lot_lineage_card_header">
                          <div className="lot_lineage_card_name">
                            <Icon icon="fa-hourglass-half" className="lot_lineage_card_icon" />
                            <span className="lot_lineage_card_name_text">{getLabel("refinement")}</span>
                          </div>
                        </div>
                        <div className="lot_lineage_card_info_grid">
                          {refinements?.map((r, idx) => (
                            <div key={`refinement-${idx}`} className="lot_lineage_info_item">
                              <Icon icon="fa-hourglass" className="lot_lineage_info_icon" />
                              <div className="lot_lineage_info_content">
                                <span className="lot_lineage_info_label">{r?.id}</span>
                                <span className="lot_lineage_info_value">
                                  {r?.min || ""} {r?.max && `- ${r?.max}`} {getLabel("refinementMonths")}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {isDetail && !mergedBatchVarieties?.length && <h4>{getLabel("lotDetails")}</h4>}
          {!isDetail && <h5>{getLabel("lotDetails")}</h5>}

          {/* COMPOSITION SECTION */}
          {isDetail && mergedBatchVarieties && mergedBatchVarieties.length > 0 && (
            <div className="lot_info_section_title">
              <Icon icon="fa-leaf" className="section_icon" />
              {getLabel("lotVarieties")}
            </div>
          )}

          <div className="lot_info_container_row">
            {mergedBatchVarieties && mergedBatchVarieties.length > 0 && (
              <>
                <Label
                  label={getLabel("lotVarieties")}
                  customClassName="lot_info_varieties"
                >
                  {mergedBatchVarieties.map((item, index) => (
                    <p key={`variety-${index}`}>
                      {`${item?.variety || "\u00A0"} ${
                        (isMergedBatchVarieties ||
                          mergedBatchVarieties.some(
                            (e, i) => !isSameVarietyOriginYearDifferentTank(),
                          )) &&
                        item.percentage &&
                        item.percentage !== 100
                          ? // ? `(${Math.round(item.percentage)}%)`
                            `(${item.percentage}%)`
                          : ""
                      }`}
                    </p>
                  ))}
                </Label>

                <Label label={getLabel("lotYears")}>
                  {mergedBatchVarieties.map((item, index) => (
                    <p key={`year-${index}`}>{item.year || ""}</p>
                  ))}
                </Label>

                <Label label={getLabel("lotOrigins")}>
                  {mergedBatchVarieties.map((item, index) => (
                    <p key={`origin-${index}`}>{item.origin || ""}</p>
                  ))}
                </Label>
                {isDetail && mergedBatchVarieties?.length > 1 && (
                  <Label label={getLabel("lotPercentageQuantity")}>
                    {mergedBatchVarieties?.map((item, index) => (
                      <p key={`comp-${index}`}>
                        {item?.quantity &&
                        (!isMergedBatchVarieties || !item?.hideVariety)
                          ? (`${Math.round(item.quantity)} ${lot?.state !== "GRAPE" ? "L" : "Kg"}` ??
                            "-")
                          : "\u00A0"}
                        {!isMergedBatchVarieties &&
                          isSameVarietyOriginYearDifferentTank() &&
                          item?.tank_name &&
                          ` (${item.tank_name})`}
                      </p>
                    ))}
                  </Label>
                )}
                {isDetail &&
                  mergedBatchVarieties &&
                  ((isMergedBatchVarieties &&
                    (mergedBatchVarieties?.length > 1 ||
                      !isSameVarietyOriginYearDifferentTank())) ||
                    (!isMergedBatchVarieties &&
                      isSameVarietyOriginYearDifferentTank())) && ( // checks if variety is the same but either origin or year is different
                    <div className="variety_merge_cta">
                      <Tooltip
                        variant={"info"}
                        html={getLabel(
                          isMergedBatchVarieties
                            ? "lotShowVarieties"
                            : "lotHideVarieties",
                        )}
                        place="right"
                        events={["hover"]}
                      >
                        <CTA
                          customClassName={`merge ${
                            !isMergedBatchVarieties ? "on" : "off"
                          }`}
                          onClick={() =>
                            setIsMergedBatchVarieties((prev) => !prev)
                          }
                        >
                          <span className="icon">
                            <Icon icon={"fa-sort-up"} />
                          </span>
                        </CTA>
                      </Tooltip>
                    </div>
                  )}
              </>
            )}
            {lot?.doco_id && lot?.doco_date && (
              <Label label={getLabel("lotDoco")}>
                {lot?.doco_id} -{" "}
                {format(new Date(lot?.doco_date), configs.shortDateFormat)}
              </Label>
            )}
          </div>
          {lot?.state === "WINE" && [0, 1, 2].includes(lot?.batch_type) && (
            <div className="lot_info_container_row start">
              <Label label={getLabel("lotType")}>
                {getLabel(`lotType${lot?.batch_type}`)}
              </Label>
            </div>
          )}

          {/* GRAPE STATE INFORMATION SECTION - CARD BASED */}
          {lot?.state === "GRAPE" && (
            <>
              {/* Harvest & Health Info Cards */}
              {lot?.harvest_conditions && <div className="lot_info_section_title">
                <Icon icon="fa-spa" className="section_icon" />
                {getLabel("lotHarvestConditionsSection")}
              </div>}

              <div className="lot_lineage_cards">
                {/* Harvest Conditions Card */}
                {lot?.harvest_conditions && hasAnyValue(lot.harvest_conditions) && (
                  <div className="lot_lineage_card lineage_accent_harvest">
                    <div className="lot_lineage_card_header">
                      <div className="lot_lineage_card_name">
                        <Icon icon="fa-leaf" className="lot_lineage_card_icon" />
                        <span className="lot_lineage_card_name_text">{getLabel("lotHarvestConditionsSection")}</span>
                      </div>
                    </div>
                    <div className="lot_lineage_card_info_grid">
                      {lot.harvest_conditions.harvest_date && (
                        <div className="lot_lineage_info_item">
                          <Icon icon="fa-calendar" className="lot_lineage_info_icon" />
                          <div className="lot_lineage_info_content">
                            <span className="lot_lineage_info_label">{getLabel("lotHarvestDate")}</span>
                            <span className="lot_lineage_info_value">
                              {format(new Date(lot.harvest_conditions.harvest_date), configs.shortDateFormat)}
                            </span>
                          </div>
                        </div>
                      )}
                      {lot.harvest_conditions.method && (
                        <div className="lot_lineage_info_item">
                          <Icon icon="fa-hand" className="lot_lineage_info_icon" />
                          <div className="lot_lineage_info_content">
                            <span className="lot_lineage_info_label">{getLabel("lotHarvestMethod")}</span>
                            <span className="lot_lineage_info_value">{getLabel(`lotHarvestMethod_${lot.harvest_conditions.method}`)}</span>
                          </div>
                        </div>
                      )}
                      {lot.harvest_conditions.container_type && (
                        <div className="lot_lineage_info_item">
                          <Icon icon="fa-box" className="lot_lineage_info_icon" />
                          <div className="lot_lineage_info_content">
                            <span className="lot_lineage_info_label">{getLabel("lotContainerType")}</span>
                            <span className="lot_lineage_info_value">{lot.harvest_conditions.container_type}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Weather Conditions Card */}
                {lot?.harvest_conditions?.weather && hasAnyValue(lot.harvest_conditions.weather) && (
                  <div className="lot_lineage_card lineage_accent_white">
                    <div className="lot_lineage_card_header">
                      <div className="lot_lineage_card_name">
                        <Icon icon="fa-cloud-sun" className="lot_lineage_card_icon" />
                        <span className="lot_lineage_card_name_text">{getLabel("lotWeather")}</span>
                      </div>
                    </div>
                    <div className="lot_lineage_card_info_grid">
                      {lot.harvest_conditions.weather.conditions && (
                        <div className="lot_lineage_info_item">
                          <Icon icon="fa-sun" className="lot_lineage_info_icon" />
                          <div className="lot_lineage_info_content">
                            <span className="lot_lineage_info_label">{getLabel("lotWeatherConditions")}</span>
                            <span className="lot_lineage_info_value">{lot.harvest_conditions.weather.conditions}</span>
                          </div>
                        </div>
                      )}
                      {lot.harvest_conditions.weather.harvest_temperature != null && (
                        <div className="lot_lineage_info_item">
                          <Icon icon="fa-temperature-half" className="lot_lineage_info_icon" />
                          <div className="lot_lineage_info_content">
                            <span className="lot_lineage_info_label">{getLabel("lotHarvestTemperature")}</span>
                            <span className="lot_lineage_info_value">{lot.harvest_conditions.weather.harvest_temperature}°C</span>
                          </div>
                        </div>
                      )}
                      {lot.harvest_conditions.weather.humidity && (
                        <div className="lot_lineage_info_item">
                          <Icon icon="fa-droplet" className="lot_lineage_info_icon" />
                          <div className="lot_lineage_info_content">
                            <span className="lot_lineage_info_label">{getLabel("lotHumidity")}</span>
                            <span className="lot_lineage_info_value">{lot.harvest_conditions.weather.humidity}%</span>
                          </div>
                        </div>
                      )}
                      {lot.harvest_conditions.weather.days_since_last_rain != null && (
                        <div className="lot_lineage_info_item">
                          <Icon icon="fa-cloud-rain" className="lot_lineage_info_icon" />
                          <div className="lot_lineage_info_content">
                            <span className="lot_lineage_info_label">{getLabel("lotDaysSinceLastRain")}</span>
                            <span className="lot_lineage_info_value">{lot.harvest_conditions.weather.days_since_last_rain} {getLabel("days")}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Transport Conditions Card */}
                {lot?.harvest_conditions?.transport && hasAnyValue(lot.harvest_conditions.transport) && (
                  <div className="lot_lineage_card lineage_accent_rose">
                    <div className="lot_lineage_card_header">
                      <div className="lot_lineage_card_name">
                        <Icon icon="fa-truck" className="lot_lineage_card_icon" />
                        <span className="lot_lineage_card_name_text">{getLabel("lotTransport")}</span>
                      </div>
                    </div>
                    <div className="lot_lineage_card_info_grid">
                      {lot.harvest_conditions.transport.time_from_harvest_hours != null && (
                        <div className="lot_lineage_info_item">
                          <Icon icon="fa-clock" className="lot_lineage_info_icon" />
                          <div className="lot_lineage_info_content">
                            <span className="lot_lineage_info_label">{getLabel("lotTimeFromHarvest")}</span>
                            <span className="lot_lineage_info_value">{lot.harvest_conditions.transport.time_from_harvest_hours}h</span>
                          </div>
                        </div>
                      )}
                      {lot.harvest_conditions.transport.transport_temperature != null && (
                        <div className="lot_lineage_info_item">
                          <Icon icon="fa-temperature-low" className="lot_lineage_info_icon" />
                          <div className="lot_lineage_info_content">
                            <span className="lot_lineage_info_label">{getLabel("lotTransportTemperature")}</span>
                            <span className="lot_lineage_info_value">{lot.harvest_conditions.transport.transport_temperature}°C</span>
                          </div>
                        </div>
                      )}
                      {lot.harvest_conditions.transport.notes && (
                        <div className="lot_lineage_info_item">
                          <Icon icon="fa-note-sticky" className="lot_lineage_info_icon" />
                          <div className="lot_lineage_info_content">
                            <span className="lot_lineage_info_label">{getLabel("lotTransportNotes")}</span>
                            <span className="lot_lineage_info_value">{lot.harvest_conditions.transport.notes}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Health Status Card */}
                {lot?.health_status && hasAnyValue(lot.health_status) && (
                  <div className="lot_lineage_card lineage_accent_red">
                    <div className="lot_lineage_card_header">
                      <div className="lot_lineage_card_name">
                        <Icon icon="fa-heart-pulse" className="lot_lineage_card_icon" />
                        <span className="lot_lineage_card_name_text">{getLabel("lotHealthStatusSection")}</span>
                      </div>
                    </div>
                    <div className="lot_lineage_card_info_grid">
                      {lot.health_status.condition && (
                        <div className="lot_lineage_info_item">
                          <Icon icon="fa-check-circle" className="lot_lineage_info_icon" />
                          <div className="lot_lineage_info_content">
                            <span className="lot_lineage_info_label">{getLabel("lotHealthCondition")}</span>
                            <span className="lot_lineage_info_value">{getLabel(`lotHealthCondition_${lot.health_status.condition}`)}</span>
                          </div>
                        </div>
                      )}
                      {lot.health_status.rot_percent != null && (
                        <div className="lot_lineage_info_item">
                          <Icon icon="fa-percent" className="lot_lineage_info_icon" />
                          <div className="lot_lineage_info_content">
                            <span className="lot_lineage_info_label">{getLabel("lotRotPercent")}</span>
                            <span className="lot_lineage_info_value">{lot.health_status.rot_percent}%</span>
                          </div>
                        </div>
                      )}
                      {lot.health_status.botrytis_percent != null && (
                        <div className="lot_lineage_info_item">
                          <Icon icon="fa-percent" className="lot_lineage_info_icon" />
                          <div className="lot_lineage_info_content">
                            <span className="lot_lineage_info_label">{getLabel("lotBotrytisPercent")}</span>
                            <span className="lot_lineage_info_value">{lot.health_status.botrytis_percent}%</span>
                          </div>
                        </div>
                      )}
                      {lot.health_status.damaged_berries_percent != null && (
                        <div className="lot_lineage_info_item">
                          <Icon icon="fa-percent" className="lot_lineage_info_icon" />
                          <div className="lot_lineage_info_content">
                            <span className="lot_lineage_info_label">{getLabel("lotDamagedBerriesPercent")}</span>
                            <span className="lot_lineage_info_value">{lot.health_status.damaged_berries_percent}%</span>
                          </div>
                        </div>
                      )}
                      {lot.health_status.leaves_stems_percent != null && (
                        <div className="lot_lineage_info_item">
                          <Icon icon="fa-percent" className="lot_lineage_info_icon" />
                          <div className="lot_lineage_info_content">
                            <span className="lot_lineage_info_label">{getLabel("lotLeavesStemsPercent")}</span>
                            <span className="lot_lineage_info_value">{lot.health_status.leaves_stems_percent}%</span>
                          </div>
                        </div>
                      )}
                      {lot.health_status.immature_berries_percent != null && (
                        <div className="lot_lineage_info_item">
                          <Icon icon="fa-percent" className="lot_lineage_info_icon" />
                          <div className="lot_lineage_info_content">
                            <span className="lot_lineage_info_label">{getLabel("lotImmatureBerriesPercent")}</span>
                            <span className="lot_lineage_info_value">{lot.health_status.immature_berries_percent}%</span>
                          </div>
                        </div>
                      )}
                      {lot.health_status.notes && (
                        <div className="lot_lineage_info_item">
                          <Icon icon="fa-note-sticky" className="lot_lineage_info_icon" />
                          <div className="lot_lineage_info_content">
                            <span className="lot_lineage_info_label">{getLabel("lotHealthNotes")}</span>
                            <span className="lot_lineage_info_value">{lot.health_status.notes}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Analysis Protocols Card */}
                {(lot?.field_analysis_protocol_id || lot?.lab_analysis_protocol_id) && (
                  <div className="lot_lineage_card">
                    <div className="lot_lineage_card_header">
                      <div className="lot_lineage_card_name">
                        <Icon icon="fa-flask-vial" className="lot_lineage_card_icon" />
                        <span className="lot_lineage_card_name_text">{getLabel("lotAnalysisProtocolsSection")}</span>
                      </div>
                    </div>
                    <div className="lot_lineage_card_info_grid">
                      {/* Field Analysis Protocol */}
                      {lot.field_analysis_protocol_id && (() => {
                        const fieldProtocol = getProtocolById(lot.field_analysis_protocol_id);
                        
                        return (
                          <div className="lot_lineage_info_item">
                            <Icon icon="fa-microscope" className="lot_lineage_info_icon" />
                            <div className="lot_lineage_info_content">
                              <span className="lot_lineage_info_label">{getLabel("lotFieldAnalysisProtocol")}</span>
                              <span className="lot_lineage_info_value">
                                <div>
                                  {lot?.field_analysis_operation_id ? (
                                    <a
                                      href={`/programming/history/${fieldProtocol?.completion_date ? "history/" : ""}analysis/${lot.field_analysis_operation_id}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{ color: "inherit", textDecoration: "none", cursor: "pointer" }}
                                      title={getLabel("goToDetail")}
                                    >
                                      {fieldProtocol?.protocols_name || lot.field_analysis_protocol_id}
                                      <Icon icon="fa-external-link-alt" style={{ marginLeft: "6px", fontSize: "12px" }} />
                                    </a>
                                  ) : (
                                    fieldProtocol?.protocols_name || lot.field_analysis_protocol_id
                                  )}
                                </div>
                                {lot?.field_analysis_collection_date && (
                                  <div style={{ fontSize: "12px", color: "#999", marginTop: "4px" }}>
                                    {format(new Date(lot.field_analysis_collection_date), "dd MMM yyyy - HH:mm", { locale: getLocaleObject(configs.locale) })}
                                  </div>
                                )}
                              </span>
                              {/* Field Analysis Results */}
                              {lot?.field_analysis && lot.field_analysis.length > 0 && (
                                <div style={{ marginTop: "8px", paddingTop: "8px", borderTop: "1px solid #e0e0e0" }}>
                                  {lot.field_analysis.map((analysis, idx) => {
                                    const analysisItem = fieldProtocol?.analysis?.find(a => a.substance_id === analysis.analysis_id);
                                    return (
                                      <div key={`field-analysis-${idx}`} style={{ fontSize: "12px", marginBottom: "4px" }}>
                                        <span style={{ fontWeight: "500" }}>
                                          {analysisItem ? getLabel(`substance_${analysisItem.substance_name}`) : `Analysis ${analysis.analysis_id}`}
                                        </span>
                                        <span style={{ marginLeft: "8px", color: "#666" }}>
                                          {analysis.value} {analysisItem?.unit || ""}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                      {/* Lab Analysis Protocol */}
                      {lot.lab_analysis_protocol_id && (() => {
                        const labProtocol = getProtocolById(lot.lab_analysis_protocol_id);
                        const labOp = getLabAnalysisOperation();
                        return (
                          <div className="lot_lineage_info_item">
                            <Icon icon="fa-vial" className="lot_lineage_info_icon" />
                            <div className="lot_lineage_info_content">
                              <span className="lot_lineage_info_label">{getLabel("lotLabAnalysisProtocol")}</span>
                              <span className="lot_lineage_info_value">
                                {labOp ? (
                                  <a
                                    href={`/programming/${labProtocol?.completion_date ? "history/" : ""}analysis/${labOp.id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: "inherit", textDecoration: "none", cursor: "pointer" }}
                                    title={getLabel("goToDetail")}
                                  >
                                    {labProtocol?.protocols_name || lot.lab_analysis_protocol_id}
                                    <Icon icon="fa-external-link-alt" style={{ marginLeft: "6px", fontSize: "12px" }} />
                                  </a>
                                ) : (
                                  labProtocol?.protocols_name || lot.lab_analysis_protocol_id
                                )}
                              </span>
                              {/* Lab Analysis Names - Show all available analyses */}
                              {labProtocol?.analysis && labProtocol.analysis.length > 0 && (
                                <div style={{ marginTop: "8px", paddingTop: "8px", borderTop: "1px solid #e0e0e0" }}>
                                  {labProtocol.analysis.map((analysis, idx) => {
                                    const resultValue = lot?.lab_analysis?.find(r => r.analysis_id === analysis.substance_id);
                                    return (
                                      <div key={`lab-analysis-${idx}`} style={{ fontSize: "12px", marginBottom: "4px" }}>
                                        <span style={{ fontWeight: "500" }}>
                                          {getLabel(`substance_${analysis.substance_name}`) || analysis.substance_name}
                                        </span>
                                        {resultValue && (
                                          <span style={{ marginLeft: "8px", color: "#666" }}>
                                            {resultValue.value} {analysis?.unit || ""}
                                          </span>
                                        )}
                                        {!resultValue && (
                                          <span style={{ marginLeft: "8px", color: "#999", fontStyle: "italic" }}>
                                            —
                                          </span>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Certifications Section - Card Style */}
          {lot?.certifications && (lot?.certifications?.organic || lot?.certifications?.vegan || lot?.certifications?.biodynamic) && (
            <>
              <div className="lot_info_section_title">
                <Icon icon="fa-certificate" className="section_icon" />
                {getLabel("lotCertificationsSection")}
              </div>
              <div className="lot_lineage_card">
                <div className="lot_lineage_card_header">
                  <div className="lot_lineage_card_name">
                    <Icon icon="fa-certificate" className="lot_lineage_card_icon" />
                    <span className="lot_lineage_card_name_text">{getLabel("lotCertificationsSection")}</span>
                  </div>
                </div>
                <div className="cert_badges_container">
                  {lot.certifications.organic && (
                    <div className="cert_badge">
                      <Icon icon="fa-leaf" />
                      {getLabel("lotCertificationOrganic")}
                    </div>
                  )}
                  {lot.certifications.vegan && (
                    <div className="cert_badge">
                      <Icon icon="fa-seedling" />
                      {getLabel("lotCertificationVegan")}
                    </div>
                  )}
                  {lot.certifications.biodynamic && (
                    <div className="cert_badge">
                      <Icon icon="fa-spa" />
                      {getLabel("lotCertificationBiodynamic")}
                    </div>
                  )}
                </div>
                {lot.certifications.documents && lot.certifications.documents.length > 0 && (
                  <div className="certification_documents" style={{ marginTop: "12px", paddingTop: "12px", borderTop: "1px solid #e0e0e0" }}>
                    <p style={{ fontSize: "12px", fontWeight: "600", marginBottom: "8px" }}>
                      {getLabel("lotCertificationDocuments")}
                    </p>
                    {lot.certifications.documents.map((doc, idx) => (
                      <div key={`cert-doc-${idx}`} className="lot_lineage_info_item">
                        <Icon icon="fa-file-alt" className="lot_lineage_info_icon" />
                        <div className="lot_lineage_info_content">
                          <span className="lot_lineage_info_label">{doc.type}</span>
                          <span className="lot_lineage_info_value">
                            {doc.number && `#${doc.number}`}
                            {doc.date && ` • ${format(new Date(doc.date), "dd/MM")}`}
                            {doc.issuing_authority && ` • ${doc.issuing_authority}`}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {lot.certifications.iso_traceability && (
                  <div style={{ marginTop: "12px", paddingTop: "12px", borderTop: "1px solid #e0e0e0" }}>
                    <p style={{ fontSize: "12px", fontWeight: "600", marginBottom: "8px" }}>
                      {getLabel("lotISOTraceability")}
                    </p>
                    {lot.certifications.iso_traceability.field_lot && (
                      <div className="lot_lineage_info_item">
                        <Icon icon="fa-map-pin" className="lot_lineage_info_icon" />
                        <div className="lot_lineage_info_content">
                          <span className="lot_lineage_info_label">{getLabel("lotFieldLot")}</span>
                          <span className="lot_lineage_info_value">{lot.certifications.iso_traceability.field_lot}</span>
                        </div>
                      </div>
                    )}
                    {lot.certifications.iso_traceability.vineyard_registry && (
                      <div className="lot_lineage_info_item">
                        <Icon icon="fa-book" className="lot_lineage_info_icon" />
                        <div className="lot_lineage_info_content">
                          <span className="lot_lineage_info_label">{getLabel("lotVineyardRegistry")}</span>
                          <span className="lot_lineage_info_value">{lot.certifications.iso_traceability.vineyard_registry}</span>
                        </div>
                      </div>
                    )}
                    {lot.certifications.iso_traceability.phytosanitary_treatments && (
                      <div className="lot_lineage_info_item">
                        <Icon icon="fa-spray-can" className="lot_lineage_info_icon" />
                        <div className="lot_lineage_info_content">
                          <span className="lot_lineage_info_label">{getLabel("lotPhytosanitaryTreatments")}</span>
                          <span className="lot_lineage_info_value">{lot.certifications.iso_traceability.phytosanitary_treatments}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}


          {/* Vinification Technique Section */}
          {(lot?.vinification_technique || lot?.tank_substates?.some(ts => ts.vinification_technique)) && (
            <div>
              {isDetail && (
                <div className="lot_info_section_title">
                  <Icon icon="fa-wine-glass" className="section_icon" />
                  {getLabel("operationSubtypeLabel")}
                </div>
              )}
              <div className="lot_info_container_row start">
                <div className="vinification_technique_wrapper">
                  {(() => {
                  // Calcola se ci sono tecniche diverse
                  const techniques = lot?.tank_substates
                    ?.map(t => t.vinification_technique)
                    .filter(t => t !== null && t !== undefined) || [];

                  const uniqueTechniques = [...new Set(techniques)];
                  const hasMixedTechniques = uniqueTechniques.length > 1;

                  // Se tecniche miste, mostra per ogni tank
                  if (hasMixedTechniques && lot?.tank_substates?.length > 0) {
                    // Raggruppa i tank per tecnica
                    const groupByTechnique = () => {
                      const groups = {};

                      lot.tank_substates.forEach(tank => {
                        if (!tank.vinification_technique) return;

                        const technique = tank.vinification_technique;

                        if (!groups[technique]) {
                          groups[technique] = {
                            technique: technique,
                            tanks: [],
                            total_quantity: 0
                          };
                        }

                        groups[technique].tanks.push(tank);
                        groups[technique].total_quantity += tank.tank_quantity || 0;
                      });

                      return Object.values(groups);
                    };

                    const techniqueGroups = groupByTechnique();

                    return (
                      <div className="vinification_technique_mixed_container">
                        <p className="vinification_technique_mixed_notice">
                          {getLabel("vinificationTechniquesMixedNotice")}
                        </p>
                        {techniqueGroups.map((group, index) => (
                          <div key={`technique-group-${index}`} className="vinification_technique_group_card">
                            <div className="vinification_technique_group_header">
                              <span className="vinification_technique_badge">
                                {getLabel(`operationSubtype${group.technique}`) || group.technique}
                              </span>
                              <span className="vinification_technique_quantity">
                                {group.total_quantity} L
                              </span>
                            </div>
                            <div className="vinification_technique_tanks">
                              <span className="vinification_technique_tanks_label">
                                {getLabel("tanks")}:
                              </span>
                              <span className="vinification_technique_tanks_list">
                                {group.tanks.map(t => t.tank_name).join(', ')}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  }

                  // Visualizzazione normale per tecnica uniforme
                  const technique = lot?.vinification_technique || uniqueTechniques[0];
                  if (technique) {
                    return (
                      <div className="vinification_technique_card">
                        <span className="vinification_technique_badge">
                          {getLabel(`operationSubtype${technique}`) || technique}
                        </span>
                      </div>
                    );
                  }

                  return null;
                  })()}
                </div>
              </div>
            </div>
          )}

          <div className="lot_info_container_row start">
            {lot?.substate && (
              <div className="substate_current_wrapper">
                {isDetail && (
                  <div className="lot_info_section_title">
                    <Icon icon="fa-diagram-project" className="section_icon" />
                    {getLabel("lotSubstate")}
                  </div>
                )}
                {!isDetail && <h5>{getLabel("lotSubstate")}</h5>}
          
                {/* Se substates misti, mostra per ogni tank */}
                {lot?.has_mixed_substates && lot?.tank_substates?.length > 0 ? (
                  <div className="substate_mixed_container">
                    <p className="substate_mixed_notice">
                      {getLabel("substatesMixedNotice") || "I tank hanno substates diversi"}
                    </p>
                    {lot.tank_substates.map((tankSub, index) => (
                      tankSub?.substate && (
                        <div key={`tank-substate-${index}`} className="substate_tank_card">
                          <div className="substate_tank_header">
                            <span className="substate_tank_name">
                              <Icon icon="fa-wine-bottle" />
                              {tankSub.tank_name}
                            </span>
                            <span className={`substate_current_badge substate_${tankSub?.substate?.toLowerCase?.()} ${
                              isSubstateInProgress(tankSub.substate) ? "started" : ""
                            }`}>
                              {isSubstateInProgress(tankSub.substate) && (
                                <span className="spinner"></span>
                              )}
                              {getLabel(`lotSubstate_${tankSub.substate}`)}
                            </span>
                          </div>
                          {tankSub.substate_started_at && (
                            <div className="substate_tank_duration">
                              <Icon icon="fa-clock" />
                              <span>
                                {formatDistanceToNow(
                                  new Date(tankSub.substate_started_at),
                                  {
                                    locale: getLocaleObject(configs.locale),
                                  },
                                )}
                              </span>
                            </div>
                          )}
                          <div className="substate_tank_quantity">
                            <Icon icon="fa-flask" />
                            <span>{tankSub.tank_quantity} L</span>
                          </div>
                        </div>
                      )
                    ))}
                  </div>
                ) : (
                  /* Visualizzazione normale per substate uniforme */
                  <div className="substate_current_card">
                    {/* Header con badge e data */}
                    <div className="substate_current_header">
                      <div className="substate_current_badge_wrapper">
                        <span className={`substate_current_badge substate_${lot.substate.toLowerCase()} ${
                          isSubstateInProgress(lot.substate) ? "started" : ""
                        }`}>
                          {isSubstateInProgress(lot.substate) && (
                            <span className="spinner"></span>
                          )}
                          {getLabel(`lotSubstate_${lot.substate}`)}
                        </span>
                        <span className="substate_current_active_indicator">
                          <Icon icon="fa-circle" className="pulse_icon" />
                          {getLabel("substateActive")}
                        </span>
                      </div>
                      {lot?.substate_started_at && (
                        <div className="substate_current_date">
                          <Icon icon="fa-calendar" />
                          <span>
                            {format(new Date(lot.substate_started_at), "dd MMM yyyy, HH:mm", {
                              locale: getLocaleObject(configs.locale),
                            })}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Durata */}
                    {lot?.substate_started_at && (
                      <div className="substate_current_duration">
                        <Icon icon="fa-clock" />
                        <span className="substate_current_duration_label">
                          {getLabel("substateDuration")}:{" "}
                        </span>
                        <span className="substate_current_duration_value">
                          {formatDistanceToNow(
                            new Date(lot.substate_started_at),
                            {
                              locale: getLocaleObject(configs.locale),
                            },
                          )}
                        </span>
                      </div>
                    )}

                    {/* Progress bar se expected_end disponibile */}
                    {lot.substate_expected_end && lot.substate_started_at && (
                      <div className="substate_current_progress_wrapper">
                        {(() => {
                          const now = new Date().getTime();
                          const end = new Date(lot.substate_expected_end).getTime();
                          const start = new Date(lot.substate_started_at).getTime();

                          const totalDuration = end - start;
                          const elapsedTime = now - start;
                          const percentage = Math.min(
                            100,
                            Math.max(0, (elapsedTime / totalDuration) * 100),
                          );
                          const remainingTime = end - now;

                          const daysRemaining = Math.floor(
                            remainingTime / (1000 * 60 * 60 * 24),
                          );
                          const hoursRemaining = Math.floor(
                            (remainingTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
                          );
                          const minutesRemaining = Math.floor(
                            (remainingTime % (1000 * 60 * 60)) / (1000 * 60),
                          );

                          const isNearEnd = percentage > 75;
                          const isOverdue = percentage >= 100;

                          return (
                            <>
                              <div className="substate_current_progress_info">
                                <span className="substate_current_progress_label">
                                  {getLabel("substateProgress")}
                                </span>
                                <span className={`substate_current_progress_percentage ${isOverdue ? 'overdue' : isNearEnd ? 'warning' : ''}`}>
                                  {percentage.toFixed(1)}%
                                </span>
                              </div>
                              <div className="substate_current_progress_bar">
                                <div
                                  className={`substate_current_progress_fill ${isOverdue ? 'overdue' : isNearEnd ? 'warning' : ''}`}
                                  style={{ width: `${Math.min(percentage, 100)}%` }}
                                ></div>
                              </div>
                              <div className="substate_current_progress_time">
                                <span className="substate_current_progress_time_label">
                                  <Icon icon="fa-hourglass-half" />
                                  {isOverdue ? getLabel("substateOverdue") : getLabel("substateRemaining")}:
                                </span>
                                <span className={`substate_current_progress_time_value ${isOverdue ? 'overdue' : ''}`}>
                                  {isOverdue
                                    ? `+${Math.abs(daysRemaining)}g ${Math.abs(hoursRemaining)}h ${Math.abs(minutesRemaining)}m`
                                    : `${daysRemaining}g ${hoursRemaining}h ${minutesRemaining}m`
                                  }
                                </span>
                                <span className="substate_current_progress_time_end">
                                  ({getLabel("substateUntil")}{" "}
                                  {format(new Date(lot.substate_expected_end), "dd MMM, HH:mm", {
                                    locale: getLocaleObject(configs.locale),
                                  })})
                                </span>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                )}

                {/* CTA per mostrare/nascondere lo storico */}
                {substates?.length > 0 && (
                  <div className="substate_history_toggle">
                    <CTA onClick={() => setShowSubstateHistory(!showSubstateHistory)}>
                      <Icon icon={showSubstateHistory ? "fa-eye-slash" : "fa-eye"} />
                      {showSubstateHistory
                        ? getLabel("substateHideHistory")
                        : getLabel("substateShowHistory")
                      }
                      {!showSubstateHistory && (
                        <span className="substate_history_count">({substates.length})</span>
                      )}
                    </CTA>
                  </div>
                )}

                {/* Selettore tank (se ci sono tank diversi e siamo in LotDetail) */}
                {showSubstateHistory && uniqueTanksForSelector.length > 0 && (
                  <div className="substate_tank_selector">
                    <p className="substate_tank_selector_label">
                      <Icon icon="fa-filter" />
                      {getLabel("substateFilterByTank") || "Filtra per tank"}:
                    </p>
                    <div className="substate_tank_options">
                      {uniqueTanksForSelector.map((tank) => (
                        <button
                          key={tank.id}
                          className={`substate_tank_option ${selectedTankIdForSubstates === tank.id ? 'active' : ''}`}
                          onClick={() => setSelectedTankIdForSubstates(tank.id)}
                        >
                          <span className="substate_tank_option_name">{tank.name}</span>
                          <span className="substate_tank_option_count">({tank.count})</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Storico (visibile solo se richiesto) */}
                {showSubstateHistory && (
                  <SubstateHistoryTimeline
                    substateHistory={filteredSubstates}
                    batchColor={lot?.color}
                    batchState={lot?.state}
                    isLoading={false}
                    error={null}
                    configs={configs}
                    getLabel={getLabel}
                  />
                )}
              </div>
            )}
          </div>
          
          {isDetail &&
            (lot?.parents?.length > 0 || lot?.children?.length > 0) && (
              <div>
                <div className="lot_info_section_title">
                  <Icon icon="fa-code-branch" className="section_icon" />
                  {getLabel("lotGenealogy")}
                </div>
                <div className="lot_info_container_row start">
                  {isDetail && lot?.parents?.length > 0 && (
                    <LotLineage
                      relatives={lot?.parents}
                      label={getLabel("lotParentName")}
                    />
                  )}
                  {isDetail && lot?.children?.length > 0 && (
                    <LotLineage
                      relatives={lot?.children}
                      label={getLabel("lotChildName")}
                    />
                  )}
                </div>
              </div>
            )}
          {!isDetail && tankList?.length > 0 && (
            <div className="lot_info_section_title">
              <Icon icon="fa-wine-bottle" className="section_icon" />
              {getLabel("lotTanks")}
            </div>
          )}

          {!["GRAPE", "BOTTLED"].includes(lot.state) && (
            <>
              {tankList?.length > 0 && isDetail && (
                <div className="lot_info_section_title">
                  <Icon icon="fa-wine-bottle" className="section_icon" />
                  {getLabel(
                    lot?.status === "PLANNED"
                      ? "lotProgrammedComposition"
                      : "lotComposition",
                  )}
                </div>
              )}
              <div
                className={`lot_info_container_row ${
                  !isDetail ? "space_evenly" : ""
                }`}
              >
                {tankList?.map((tank, index) => {
                  return (
                    <div className={isDetail ? "tanks_list" : "tanks_list_reduced"} key={`tank-detail-${index}`}>
                      <DestTankDetail
                        tank={getTankData(tank)}
                        isCurrentStateOnly={true}
                        isLotDetail={true}
                        isName={false}
                        isDrop={false}
                      />
                      {/* <RecapTankIcon tank={tank} wineColor={wineColor} /> */}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {isDetail && lot?.state !== "GRAPE" && (
            <div>
              <div className="lot_info_section_title">
                <Icon icon="fa-flask" className="section_icon" />
                {getLabel("lotWineLabel")}
              </div>
              <TreatmentsLabel
                tankList={tankList}
                drugConcentrations={drugConcentrations}
                isDrugConcentration={isDrugConcentration}
                setIsDrugConcentration={setIsDrugConcentration}
                isAdjuvant={isAdjuvant}
                setIsAdjuvant={setIsAdjuvant}
                labelTankId={labelTankId}
                setLabelTankId={setLabelTankId}
                filteredDrugs={filteredDrugs}
                isTankDetail={true}
                hideTitle={true}
              />
            </div>
          )}
        </div>
      </div>

      {isDetail && lot?.note && (
        <div className="lot_info_container_row">
          <div style={{ width: "100%" }}>
            <div className="lot_info_section_title">
              <Icon icon="fa-sticky-note" className="section_icon" />
              {getLabel("lotNotes")}
            </div>
            <Label
              width={400}
              height={150}
              label={""}
              customClassName={"notes"}
            >
              {lot?.note}
            </Label>
          </div>
        </div>
      )}
    </div>
  );
};

const LotDetail = () => {
  const activeWinery = useSelector(selectActiveWinery);
  const { id } = useParams();
  const operations = useSelector(selectOperations);
  const lots = useSelector(selectLots);
  const tanks = useSelector(selectTanks);
  const users = useSelector(selectUsers);
  const configs = useSelector(selectConfigs);
  const wineLabels = useSelector(selectWineLabels);
  const [getLabel] = useLabels();
  const dispatch = useDispatch();
  const axiosPrivate = useAxiosPrivate();
  const [isHistory, setIsHistory] = useState(false); // solo questo serve
  const [lot, setLot] = useState({});
  const [isOpen, setIsOpen] = useState(false);
  const [tankList, setTankList] = useState([]);
  const [substates, setSubstates] = useState([]);
  const navigate = useNavigate();
  const { auth } = useAuth();
  const role = auth?.role || ROLES.GUEST;
  const pageName = getLabel("lotTitlePage", { name: lot?.name || id });

  const columns = useMemo(() => [
    {
      Header: getLabel("operationType"),
      accessor: (row) => getLabel(`operationType${row.type}`),
    },
    {
      Header: getLabel("priority"),
      accessor: (row) => getLabel(`operationPriorityLong${row.priority}`),
    },
    {
      Header: getLabel("expiringDate"),
      accessor: (row) =>
        row.expire_date
          ? format(new Date(row.expire_date), configs.shortDateFormat)
          : "",
    },
    {
      Header: getLabel("operationSrcTank"),
      accessor: (row) =>
        row?.type === "DESTEMMING" ||
        row?.src_tanks?.find((tank) => tank?.batch_id) ? (
          <BatchColumn lots={lots} rowData={row?.src_tanks} type={row?.type} />
        ) : (
          <TankColumn rowData={row?.src_tanks} tanks={tanks} />
        ),
    },
    {
      Header: getLabel("operationDestTank"),
      accessor: (row) =>
        row?.dest_tanks?.find((tank) => tank?.batch_id) ? (
          <BatchColumn lots={lots?.dest_tanks} rowData={row} />
        ) : (
          <TankColumn rowData={row?.dest_tanks} tanks={tanks} />
        ),
    },
    //  {
    //     Header: getLabel('operationCompletionDate'),
    //     accessor: (row) => row.completion_date ? format(new Date(row.completion_date), configs.shortDateFormat) : '-'
    // }, {
    //     Header: getLabel('operationNotes'),
    //     accessor: (row) => row.notes
    // }
  ]);

  if (role === ROLES.CLIENT)
    columns.push({
      Header: getLabel("cellarman"),
      accessor: (row) => (
        <CellarmanColumn rowData={row?.cellarman_ids} users={users} />
      ),
    });

  useEffect(() => {
    if (!lots || lots.length === 0) {
      loadLots();
    }
    if (!tanks || tanks.length === 0) {
      loadTanks();
    }
    if (!operations || operations.length === 0) {
      loadOperations();
    }
    if (!wineLabels || wineLabels.length === 0) {
      loadWineLabels();
    }
    loadLotSubstates();

    // Scroll to top quando cambia l'id
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    // Carica il lotto e i dati di arrival in sequenza senza race conditions
    let isMounted = true;

    const loadLotData = async () => {
      // Reset stato quando cambia l'id
      setLot({});
      setTankList([]);
      setSubstates([]);
      setIsHistory(false);

      try {
        let loadedLot = null;
        let isHistoric = false;

        // 1. Cerca il lotto in lots
        if (lots?.length > 0) {
          loadedLot = lots.find((dbLot) => dbLot.id === Number(id));
        }

        // 2. Se non trovato, carica dal database storico
        if (!loadedLot) {
          const foundLot = await getLotHistory(activeWinery.id, id, axiosPrivate);
          if (foundLot?.success && foundLot?.updated_data) {
            loadedLot = foundLot.updated_data;
            isHistoric = true;
          }
        }

        // 3. Setta il lotto
        if (isMounted && loadedLot) {
          setLot(loadedLot);
          setIsHistory(isHistoric);

          // 4. Se GRAPE, carica dati di arrival
          if (loadedLot?.state === "GRAPE" && loadedLot?.id) {
            try {
              const arrivalData = await getBatchArrival(loadedLot.id, axiosPrivate);
              if (isMounted && arrivalData) {
                setLot((prevLot) => ({
                  ...prevLot,
                  ...arrivalData,
                }));
              }
            } catch (error) {
              console.error("Error loading batch arrival data:", error);
            }
          }
        }
      } catch (err) {
        console.error("Error during lot loading:", err);
      }
    };

    loadLotData();

    // Cleanup: evita race condition se cambia l'id mentre le richieste sono in corso
    return () => {
      isMounted = false;
    };
  }, [id, activeWinery.id, axiosPrivate, lots]);

  useEffect(() => {
    const filteredSet = [];
    if (isHistory) {
      setTankList([]);
      return;
    }

    lot?.tanks?.forEach((tank) => {
      const currentTank = tanks.find((t) => t?.id === tank?.tank_id) || {};
      const bType =
        currentTank?.batch_type !== undefined &&
        currentTank?.batch_type !== null
          ? currentTank.batch_type
          : currentTank?.planned_batch_type;

      const batchType = ![0, 1, 2].includes(bType)
        ? null
        : currentTank?.state === "WINE"
          ? getLabel(`lotType${bType}`)
          : null;
      filteredSet.push({ ...currentTank, ...tank, batch_type: batchType });
    });

    setTankList(filteredSet);
  }, [lot]);

  const handleOpenModal = () => {
    setIsOpen(true);
  };

  const handleCloseModal = () => {
    setIsOpen(false);
  };

  const handleFormSubmit = async () => {
    await removeLot();
    handleCloseModal();
  };

  const loadLots = async () => {
    const currentLots = await getLots(activeWinery.id, axiosPrivate);
    dispatch(setLots(currentLots));
  };

  const loadTanks = async () => {
    const currentTanks = await getTanks(activeWinery.id, axiosPrivate);
    dispatch(setTanks(currentTanks));
  };

  const loadOperations = async () => {
    const loadedOperations = await getOperations(axiosPrivate);
    // TO DO prendi tutte le operazioni e poi dividi tramite i selectors quelle expired e quelle ok
    // dispatch(setOperations(loadedOperations?.filter((o) => new Date(o?.expire_date) > new Date())));
    dispatch(setOperations(loadedOperations));
  };

  const loadWineLabels = async () => {
    const currentWineLabels = await getWineLabels(axiosPrivate);
    dispatch(setWineLabels(currentWineLabels));
  };  

  const loadLotSubstates = async () => {
    const currentSubstates = await getLotSubstates(axiosPrivate, id);
    setSubstates(currentSubstates);
  };

  const customIdDeleteLot = "customid-delete-lot";
  const removeLot = async () => {
    const requestPayload = {
      ...(id && { id: Number(id) }),
    };

    const response = await toastAxios(
      () => deleteLot(id, axiosPrivate),
      customIdDeleteLot,
      { name: lot?.name || "" },
      getLabel,
    );

    if (response && response?.success) {
      dispatch(removeL(requestPayload));
      if (response?.affected_entities) {
        const cacheManager = new CacheManager(dispatch, axiosPrivate);
        await cacheManager.processAffectedEntities(response.affected_entities);
      }
      navigate(`/lots/`);
    }
  };

  const renderLastAnalysis = () => {
    return (
      <div className="last_analysis_wrapper">
        <div className="last_analysis_grid">
          <AnalysisGrid id={id} isLot={true} />
        </div>
        <div className="last_analysis_button">
          <NavLink to={"/programming/analysis/new"}>
            <CTA>{getLabel("programOperationANALYSIS")}</CTA>
          </NavLink>
        </div>
      </div>
    );
  };

  const renderNavMenu = () => {
    const navClickHandler = () => {
      navigate(isHistory ? -1 : "/lots");
    };
    return (
      <div className="primary_container_menu">
        <div className="primary_container_row">
          <Button arrowDirection="left" onClick={navClickHandler} />
          <h2>{pageName}</h2>
        </div>
        <div className="primary_container_row ctas">
          {!isHistory && (
            <NavLink to={`/lots/${id}/update`}>
              <CTA>
                {getLabel("updateLot")} <Icon icon="fa-pen" />
              </CTA>
            </NavLink>
          )}
          <NavLink to={`/lots/${id}/tracking`}>
            <CTA>
              {getLabel("showTrackData")} <Icon icon="fa-timeline" />
            </CTA>
          </NavLink>
          {/* <NavLink to={`/lots/${id}/tree`}>
            <CTA>
              {getLabel("showTreeTrackData")}{" "}
              <Icon icon="fa-folder-tree" />
            </CTA>
          </NavLink> */}
          {!isHistory && (
            <NavLink to={`/lots/${id}/duplicate`}>
              <CTA>
                {getLabel("copyLot")} <Icon icon="fa-clone" />
              </CTA>
            </NavLink>
          )}
          {!isHistory && (
            <CTA onClick={handleOpenModal}>
              {getLabel("deleteLot")} <Icon icon="fa-remove" />
            </CTA>
          )}
          <ConfirmationModal
            isOpen={isOpen}
            onConfirm={handleFormSubmit}
            onClose={handleCloseModal}
            description={getLabel("modalDeleteLotDescription", {
              name: lot?.name || "",
            })}
          ></ConfirmationModal>
        </div>
      </div>
    );
  };

  const filterOpsByState = () => {
    // save them in the localStorage
    localStorage.setItem(
      "selectedInputLotOperation",
      JSON.stringify({ type: "lot", id: id }),
    );

    if (lot?.state === "GRAPE") {
      return [
        {
          label: getLabel("operationTypeANALYSIS"),
          handler: () => navigate("/programming/analysis/new"),
        },
        {
          label: getLabel("operationTypeDESTEMMING"),
          handler: () => navigate("/programming/destemming/new"),
        },
      ];
    } else if (lot?.state === "MUST" || lot?.state === "WINE") {
      return [
        {
          label: getLabel("operationTypeANALYSIS"),
          handler: () => navigate("/programming/analysis/new"),
        },
        {
          label: getLabel("operationTypeCUT"),
          handler: () => navigate("/programming/cut/new"),
        },
        {
          label: getLabel("operationTypeDECANT"),
          handler: () => navigate("/programming/decant/new"),
        },
        ...(lot?.state === "MUST"
          ? [
              {
                label: getLabel("operationTypeRACKING"),
                handler: () => navigate("/programming/racking/new"),
              },
              {
                label: getLabel("operationTypeFILTERING"),
                handler: () => navigate("/programming/filtering/new"),
              },
              {
                label: getLabel("operationTypeWINEMAKING"),
                handler: () => navigate("/programming/winemaking/new"),
              },
            ]
          : []),
        ...(lot?.state === "WINE"
          ? [
              {
                label: getLabel("operationTypeWINEMAKINGPOST"),
                handler: () => navigate("/programming/winemakingpost/new"),
              },
              {
                label: getLabel("operationTypeBOTTLING"),
                handler: () => navigate("/programming/bottling/new"),
              },
            ]
          : []),
      ];
    } else if (lot?.state === "BOTTLED") {
      return [];
    }
  };

  const renderNextOperation = () => {
    return (
      <div>
        <NextOperations id={id} type={"LOT"} />
        <div className="button-margin">
          <Dropdown
            label={getLabel("programOperation")}
            options={filterOpsByState()}
          ></Dropdown>
        </div>
      </div>
    );
  };

  return (
    <div className="primary_container">
      <SecondaryBar
        breadCrumb={[
          { label: getLabel("lotsNavLink"), link: "/lots" },
          getLabel("lotTitlePage", { name: "" }),
        ]}
      />
      {renderNavMenu()}
      <LotInfo
        lot={lot}
        tankList={tankList}
        substates={substates}
        wineLabels={wineLabels}
        isHistory={isHistory}
      />
      {!isHistory && renderNextOperation()}
      {!isHistory && renderLastAnalysis()}
    </div>
  );
};

export default LotDetail;
